import Bowser from "bowser";

import type { UserInfoResponse } from "@storyboard/web-client/auth";
import { createLogger } from "@/logger";

const log = createLogger("electron");

import { useCallback, useEffect, useState } from "react";
import { type NavigateFunction, useNavigate, useParams } from "react-router-dom";

import { setPushNotificationToken } from "@storyboard/web-client";
import { ampli } from "@/config";
import { transport } from "@/config.ts";
import { firebaseConfig, vapidKey } from "@/hooks/firebase/config";
import { ipcRendererCallback, ipcRendererSend, removeIpcListener } from "@/utils";

type ServerNotification = {
  data: {
    contentId: string;
    accountId: string;
    suggestedTitle: string;
    type: string;
    suggestedDescription: string;
    deviceToken: string;
    workspaceId: string;
    link?: string;
    body: string;
    channelItemId: string;
    icon: string;
    channelId: string;
    title: string;
    silent?: string;
  };
  from: string;
  priority: string;
  fcmMessageId: string;
  changeWorkspace?: boolean;
};
const SEND_INITIAL_NOTIFICATION = "send-initial-notification";
const START_SERVICE = "PUSH_RECEIVER:::START_NOTIFICATION_SERVICE";
const SERVICE_STARTED = "PUSH_RECEIVER:::NOTIFICATION_SERVICE_STARTED";
const SERVICE_ERROR = "PUSH_RECEIVER:::NOTIFICATION_SERVICE_ERROR";
const TOKEN_UPDATED = "PUSH_RECEIVER:::TOKEN_UPDATED";
const NOTIFICATION_RECEIVED = "PUSH_RECEIVER:::NOTIFICATION_RECEIVED";
const NEW_NOTIFICATION = "new-notification";
const NEW_NOTIFICATION_CLICKED = "notification-clicked";
const NEW_NOTIFICATION_CLICKED_CHANGE_WORKSPACE = "notification-clicked-change-workspace";
const startPayload = {
  apiKey: firebaseConfig.apiKey,
  appId: firebaseConfig.appId,
  projectId: firebaseConfig.projectId,
  vapidKey: vapidKey,
};

/** Resolve push target to in-app path (FCM may send an absolute app URL; Electron may be another origin). */
function applyInAppUrl(url: string): { pathname: string; search: string; hash: string } {
  const parsed = new URL(url, window.location.origin);
  return {
    pathname: parsed.pathname,
    search: parsed.search,
    hash: parsed.hash,
  };
}

function navigateFromPushClick(navigate: NavigateFunction, url: string) {
  try {
    const { pathname, search, hash } = applyInAppUrl(url);
    navigate({
      pathname,
      search,
      ...(hash.length > 0 ? { hash } : {}),
    });
  } catch {
    navigate(url);
  }
}

function assignFromPushClick(url: string) {
  try {
    const { pathname, search, hash } = applyInAppUrl(url);
    window.location.assign(`${pathname}${search}${hash}`);
  } catch {
    window.location.assign(url);
  }
}

export default function ElectronNotifications({ user, tokens }: { user: UserInfoResponse; tokens: string[] }) {
  const [electronToken, setElectronToken] = useState<string | null>(null);
  const [serverTokenSet, setServerTokenSet] = useState<boolean>(false);
  const browser = Bowser.getParser(window.navigator.userAgent);
  const friendlyName = `${browser.getBrowserName()} ${browser.getBrowserVersion()} ${browser.getOSName()} ${browser.getOSVersion()} ${browser.getPlatformType()}`;
  const navigate = useNavigate();
  const accountId = user?.accounts[0]?.id ?? null;
  const { workspaceId } = useParams();
  const saveTokenValue = useCallback(
    async (token: string) => {
      ampli.hasToken({ token });
      const payload = {
        accountId: accountId,
        deviceToken: token,
        friendlyName: friendlyName,
      };
      return await setPushNotificationToken(transport, payload);
    },
    [accountId, electronToken, friendlyName],
  );

  const onServiceStarted = useCallback((_: any, token: string) => setElectronToken(token), [setElectronToken]);

  const onTokenUpdated = useCallback(
    (_: any, token: string) => {
      setElectronToken(() => token);
      saveTokenValue(token).then((data) => log.debug("Token saved", data));
    },
    [setElectronToken, saveTokenValue],
  );

  const onNotificationReceived = useCallback(
    (_: any, notification: ServerNotification) => {
      if (notification.data.accountId !== accountId || notification?.data?.silent === "true") {
        return;
      }
      if (notification?.data?.workspaceId !== workspaceId) {
        ipcRendererSend(NEW_NOTIFICATION, { ...notification, changeWorkspace: true });
      } else {
        ipcRendererSend(NEW_NOTIFICATION, notification);
      }
    },
    [workspaceId, accountId],
  );

  const onNotificationClicked = useCallback(
    (_: any, url: string) => {
      navigateFromPushClick(navigate, url);
    },
    [navigate],
  );

  const onNotificationClickedChangeWorkspace = useCallback((_: any, url: string) => {
    assignFromPushClick(url);
  }, []);

  const onServiceError = useCallback((_: any, error: Error) => {
    console.error("--- Application Notification Service Error ---");
    log.error("Notification error", error);
    ampli.failedToRetrieveToken({ error });
  }, []);

  useEffect(() => {
    if (!accountId) {
      return;
    }
    ipcRendererCallback(NEW_NOTIFICATION_CLICKED, onNotificationClicked);
    ipcRendererCallback(NEW_NOTIFICATION_CLICKED_CHANGE_WORKSPACE, onNotificationClickedChangeWorkspace);
    return () => {
      removeIpcListener(NEW_NOTIFICATION_CLICKED, onNotificationClicked);
      removeIpcListener(NEW_NOTIFICATION_CLICKED_CHANGE_WORKSPACE, onNotificationClickedChangeWorkspace);
    };
  }, [accountId, onNotificationClicked, onNotificationClickedChangeWorkspace]);

  useEffect(() => {
    if (!workspaceId || !accountId) {
      return;
    }
    ipcRendererSend(START_SERVICE, startPayload);
    ipcRendererSend(SEND_INITIAL_NOTIFICATION, { title: "Storyboard Chat", body: "Welcome to storyboard chat!" });
    ipcRendererCallback(NOTIFICATION_RECEIVED, onNotificationReceived);
    ipcRendererCallback(SERVICE_ERROR, onServiceError);
    ipcRendererCallback(SERVICE_STARTED, onServiceStarted);
    ipcRendererCallback(TOKEN_UPDATED, onTokenUpdated);

    return () => {
      removeIpcListener(NOTIFICATION_RECEIVED, onNotificationReceived);
      removeIpcListener(SERVICE_ERROR, onServiceError);
      removeIpcListener(SERVICE_STARTED, onServiceStarted);
      removeIpcListener(TOKEN_UPDATED, onTokenUpdated);
    };
  }, [accountId, onNotificationReceived, onServiceError, onServiceStarted, onTokenUpdated, workspaceId]);

  useEffect(() => {
    if (user?.accounts?.length > 0 && electronToken && !serverTokenSet) {
      const hasToken = tokens?.some((token) => token === electronToken);
      if (hasToken === false) {
        setServerTokenSet(() => true);
        saveTokenValue(electronToken).then((data) => log.debug("Token saved", data));
      }
    }
  }, [user, electronToken, friendlyName, serverTokenSet]);

  useEffect(() => {
    if (electronToken) {
      log.debug("Electron token", electronToken);
    }
  }, [electronToken]);

  return <></>;
}
